/**
 * TagGrid Component
 * 
 * Grid/list display with color badges
 * Following the design specifications
 * 
 * Features:
 * - Grid or list view toggle
 * - Color-coded tag badges
 * - Selection support
 * - WCAG 2.2 AA compliant
 */

import React from 'react';
import { Edit, Trash2 } from 'lucide-react';
import { Tag } from '../../../../types/tag.types';

interface TagGridProps {
  tags: Tag[];
  onEdit: (tag: Tag) => void;
  onDelete: (tag: Tag) => void;
  selectedIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  viewMode?: 'grid' | 'list';
}

export function TagGrid({
  tags,
  onEdit,
  onDelete,
  selectedIds = new Set(),
  onSelectionChange,
  viewMode = 'grid'
}: TagGridProps): React.ReactElement {
  const handleSelect = (tagId: string, checked: boolean) => {
    if (!onSelectionChange) return;
    
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(tagId);
    } else {
      newSelected.delete(tagId);
    }
    onSelectionChange(newSelected);
  };

  if (tags.length === 0) {
    return (
      <div className="p-12 text-center">
        <p className="text-gray-500 dark:text-gray-400">No tags found</p>
      </div>
    );
  }

  if (viewMode === 'grid') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tags.map((tag) => {
          const isSelected = selectedIds.has(tag.id);
          return (
            <div
              key={tag.id}
              className={`p-4 border rounded-lg transition-colors ${
                isSelected
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center space-x-3">
                {onSelectionChange && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => handleSelect(tag.id, e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    aria-label={`Select ${tag.name}`}
                  />
                )}
                <div
                  className="w-6 h-6 rounded-full flex-shrink-0"
                  style={{ backgroundColor: tag.color }}
                  aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                    {tag.name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {/* TODO: Add transaction count when transactions feature is implemented */}
                    0 transactions
                  </p>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => onEdit(tag)}
                    className="p-1 text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label={`Edit ${tag.name}`}
                  >
                    <Edit className="w-4 h-4" aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => onDelete(tag)}
                    className="p-1 text-gray-500 hover:text-red-600 dark:hover:text-red-400 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label={`Delete ${tag.name}`}
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-2">
      {tags.map((tag) => {
        const isSelected = selectedIds.has(tag.id);
        return (
          <div
            key={tag.id}
            className={`flex items-center space-x-4 p-4 border rounded-lg transition-colors ${
              isSelected
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            {onSelectionChange && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => handleSelect(tag.id, e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                aria-label={`Select ${tag.name}`}
              />
            )}
            <div
              className="w-6 h-6 rounded-full flex-shrink-0"
              style={{ backgroundColor: tag.color }}
              aria-hidden="true"
            />
            <div className="flex-1">
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {tag.name}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {/* TODO: Add transaction count when transactions feature is implemented */}
                0 transactions
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onEdit(tag)}
                className="p-1 text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label={`Edit ${tag.name}`}
              >
                <Edit className="w-4 h-4" aria-hidden="true" />
              </button>
              <button
                onClick={() => onDelete(tag)}
                className="p-1 text-gray-500 hover:text-red-600 dark:hover:text-red-400 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label={`Delete ${tag.name}`}
              >
                <Trash2 className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

